import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Loader2, RefreshCw, RotateCcw, CheckCircle2, XCircle, Zap,
} from "lucide-react";

const money = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD" })
    : "—";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function RuleHistoryDialog({ rule, open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [confirmRetry, setConfirmRetry] = useState(false);
  const [entries, setEntries] = useState([]);
  const [ruleMeta, setRuleMeta] = useState(null);

  const load = useCallback(async () => {
    if (!rule?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/job-costing/auto-capture/rules/${rule.id}/history`);
      setEntries(data.history || []);
      setRuleMeta(data.rule || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [rule?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const doRetry = async () => {
    setConfirmRetry(false);
    setRetrying(true);
    try {
      const { data } = await api.post(`/job-costing/auto-capture/rules/${rule.id}/retry`);
      if (data.ok) {
        toast.success(
          `Retry OK — ${data.rows_upserted} row(s) written, total ${money(data.amount || 0)}`,
        );
      } else {
        toast.error(`Retry failed — ${data.error || "unknown error"}`);
      }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl" data-testid="rule-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#FF5A00]" /> Rule execution history
            </DialogTitle>
            <DialogDescription>
              {rule?.label ? (
                <>
                  Last 20 executions for <span className="font-mono-accent">{rule.label}</span> (newest first).
                  Retry re-runs against the most recent job and writes cost lines.
                </>
              ) : (
                "Last 20 executions (newest first)."
              )}
            </DialogDescription>
          </DialogHeader>

          {ruleMeta && (
            <div className="text-xs text-zinc-500 border border-zinc-100 bg-zinc-50 rounded-md px-3 py-2 flex flex-wrap gap-x-4 gap-y-1">
              <span>Adapter: <span className="font-mono-accent text-zinc-700">{ruleMeta.kind}</span></span>
              <span>Category: <span className="text-zinc-700">{ruleMeta.category}</span></span>
              <span>Last run: {when(ruleMeta.last_run_at)}</span>
              <span>Last result: {ruleMeta.last_result || "—"}</span>
              <span>Last amount: {money(ruleMeta.last_amount)}</span>
            </div>
          )}

          <div className="max-h-[420px] overflow-y-auto border border-zinc-100 rounded-md" data-testid="rule-history-list">
            {loading ? (
              <div className="p-8 text-center text-zinc-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
              </div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center text-zinc-400 text-sm" data-testid="rule-history-empty">
                No executions recorded yet. Run the rule from a job or click Retry to generate history.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 border-b border-zinc-100 sticky top-0 bg-white">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left">Result</th>
                    <th className="text-right">Rows</th>
                    <th className="text-right">Amount</th>
                    <th className="text-left px-3">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={i} className="border-b border-zinc-100 last:border-0 align-top"
                        data-testid={`rule-history-row-${i}`}>
                      <td className="px-3 py-2 text-xs text-zinc-600 whitespace-nowrap">{when(e.at)}</td>
                      <td>
                        {e.result === "ok" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> ok
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-700 text-xs">
                            <XCircle className="w-3.5 h-3.5" /> error
                          </span>
                        )}
                        {e.trigger && (
                          <div className="text-[10px] text-zinc-400 font-mono-accent uppercase mt-0.5">{e.trigger}</div>
                        )}
                      </td>
                      <td className="text-right text-xs text-zinc-700 tabular-nums">{e.rows ?? 0}</td>
                      <td className="text-right text-xs text-zinc-700 tabular-nums">{money(e.amount)}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500 max-w-[280px] break-words">
                        {e.error ? (
                          <span className="text-rose-600" data-testid={`rule-history-error-${i}`}>{e.error}</span>
                        ) : e.job_id ? (
                          <span className="font-mono-accent">job {e.job_id.slice(-6)}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={load}
              disabled={loading}
              data-testid="rule-history-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              onClick={() => setConfirmRetry(true)}
              disabled={retrying || loading}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
              data-testid="rule-history-retry-btn"
            >
              {retrying ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Retrying…</>
              ) : (
                <><RotateCcw className="w-4 h-4 mr-1" /> Retry now</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmRetry} onOpenChange={setConfirmRetry}>
        <AlertDialogContent data-testid="rule-history-retry-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Retry this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Retry runs <span className="font-medium">{rule?.label}</span> against the most recent job for
              your business and <strong>writes cost lines</strong> to that job's cost sheet.
              Existing lines with the same source reference are safely overwritten (idempotent).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rule-history-retry-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRetry}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
              data-testid="rule-history-retry-confirm-btn"
            >
              Yes, retry now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
